import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown } from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import { EventStatusBadge } from '@/client/components/ui/event-status-badge';
import type { UpcomingStreamResponse } from '@shared/types';
import { ClientNameCell, EventIDCell, EventLinksCell, EncoderIDCell } from './cells';
import { formatDateTimeWithClientTZ } from '@/client/lib/date';

export const upcomingStreamsColumns: ColumnDef<UpcomingStreamResponse>[] = [
  {
    accessorKey: 'clientName',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Client
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const clientName = row.getValue('clientName') as string | null;
      if (!clientName) return '-';
      return <ClientNameCell clientID={row.original.clientID} clientName={clientName} />;
    },
  },
  {
    accessorKey: 'eventID',
    size: 80,
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          EventID
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => (
      <EventIDCell eventID={row.getValue('eventID')} />
    ),
  },
  {
    accessorKey: 'eventTitle',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Title
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => row.getValue('eventTitle') || '-',
  },
  {
    accessorKey: 'startDateTimePT',
    size: 200,
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Start Date/Time
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const dateStr = row.getValue('startDateTimePT') as string | null;
      const clientTimeZone = row.original.clientTimeZone;
      if (!dateStr) return '-';
      return <span className="font-mono text-xs whitespace-nowrap">{formatDateTimeWithClientTZ(dateStr, clientTimeZone)}</span>;
    },
  },
  {
    accessorKey: 'encoderID',
    size: 80,
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Encoder
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const encoderID = row.getValue('encoderID') as string | null;
      if (!encoderID) return <Badge variant="default">VOD</Badge>;
      return <EncoderIDCell encoderID={encoderID} />;
    },
  },
  {
    accessorKey: 'status',
    size: 80,
    header: 'Status',
    cell: ({ row }) => <EventStatusBadge status={row.getValue('status') as string} />,
  },
  {
    id: 'eventLinks',
    header: 'Links',
    cell: ({ row }) => (
      <EventLinksCell
        eventID={row.original.eventID}
        clientID={row.original.clientID}
        clientName={row.original.clientName ?? undefined}
      />
    ),
    size: 70,
    enableSorting: false,
  },
];
